import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AppStateContext } from './AppStateContext';
import { AuthContext } from './AuthContext';
import { useGetStdDevLevels } from '../hooks/useBackend';
import { LineStyle } from 'lightweight-charts';
import { colors } from '../theme/colors';

const StdDevContext = createContext();

export const useStdDevContext = () => {
  const context = useContext(StdDevContext);
  return context;
};

export const StdDevProvider = ({ children }) => {
  const [timeframe, setTimeframe] = useState('1hour');
  const [stdDevLevels, setStdDevLevels] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const { selectedTicker, showStdDevLines } = useContext(AppStateContext); // Get selectedTicker and showStdDevLines from AppStateContext
  const { user } = useContext(AuthContext);
  const { getStdDevLevels } = useGetStdDevLevels();
  
  // Fetch std dev levels
  const fetchStdDevLevels = useCallback(async (ticker = selectedTicker, selectedTimeframe = timeframe) => {
    if (!ticker || !user?.token) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await getStdDevLevels(user.token, ticker, selectedTimeframe);
      
      if (response && !response.error) {
        setStdDevLevels(response);
      } else {
        setError('Failed to fetch std dev levels');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.token, selectedTicker, timeframe, getStdDevLevels]);
  
  // Fetch when ticker or timeframe changes, but ONLY if std dev lines are visible
  // This prevents unnecessary fetches when toggling targeting mode if std dev lines aren't shown
  useEffect(() => {
    if (!selectedTicker || !user?.token || !showStdDevLines) return;

    fetchStdDevLevels(selectedTicker, timeframe);
  }, [user?.token, selectedTicker, timeframe, showStdDevLines, fetchStdDevLevels]);
  
  // Get price levels for chart
  const getStdDevPriceLevels = useCallback(() => {
    if (!stdDevLevels) return [];

    const levels = [
      {
        price: parseFloat(stdDevLevels.std_dev_2_upper),
        color: colors.primary, // Mint bluish green
        lineWidth: 1,
        lineStyle: LineStyle.Solid,
        axisLabelVisible: true,
        title: '+2 Std Dev',
      },
      {
        price: parseFloat(stdDevLevels.std_dev_1_5_upper),
        color: colors.primaryLight, // Lighter mint
        lineWidth: 1,
        lineStyle: LineStyle.Solid,
        axisLabelVisible: true,
        title: '+1.5 Std Dev',
      },
      {
        price: parseFloat(stdDevLevels.std_dev_1_upper),
        color: colors.primaryLighter, // Darker mint
        lineWidth: 1,
        lineStyle: LineStyle.Solid,
        axisLabelVisible: true,
        title: '+1 Std Dev',
      },
      {
        price: parseFloat(stdDevLevels.std_dev_1_lower),
        color: colors.primaryLighter, // Darker mint
        lineWidth: 1,
        lineStyle: LineStyle.Solid,
        axisLabelVisible: true,
        title: '-1 Std Dev',
      },
      {
        price: parseFloat(stdDevLevels.std_dev_1_5_lower),
        color: colors.primaryLight, // Lighter mint
        lineWidth: 1,
        lineStyle: LineStyle.Solid,
        axisLabelVisible: true,
        title: '-1.5 Std Dev',
      },
      {
        price: parseFloat(stdDevLevels.std_dev_2_lower),
        color: colors.primary, // Mint bluish green
        lineWidth: 1,
        lineStyle: LineStyle.Solid,
        axisLabelVisible: true,
        title: '-2 Std Dev',
      },
    ];
    
    // Add the reference price line if it exists
    if (stdDevLevels.reference_price) {
      levels.push({
        price: parseFloat(stdDevLevels.reference_price),
        color: colors.white, // White - middle reference (unchanged)
        lineWidth: 1,
        lineStyle: LineStyle.Solid,
        axisLabelVisible: true,
        title: 'Mid',
      });
    }

    // Filter out any levels with invalid prices (NaN, null, undefined)
    const validLevels = levels.filter(level => {
      const isValid = !isNaN(level.price) && level.price !== null && level.price !== undefined;
      return isValid;
    });

    return validLevels;
  }, [stdDevLevels]);

  const value = {
    timeframe,
    setTimeframe,
    stdDevLevels,
    loading,
    error,
    fetchStdDevLevels,
    getStdDevPriceLevels,
  };

  return (
    <StdDevContext.Provider value={value}>
      {children}
    </StdDevContext.Provider>
  );
}; 